import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { FormActions } from "../../components/FormActions";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { InfoBox } from "../../components/InfoBox";
import { Toggle } from "../../components/Toggle";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import {
  catalogDomainPolicyKey,
  defaultCatalogPolicy,
  defaultDomainPolicy,
  type CatalogDomainPolicy,
  type CatalogPolicy,
} from "@KassaroAB/shared/types/catalogPolicy";
import { CatalogDomain } from "@KassaroAB/shared/types/enums/CatalogDomain";
import { CatalogOverridableField } from "@KassaroAB/shared/types/enums/CatalogOverridableField";

const DomainList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const DomainCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
`;

const DomainTitle = styled.h3`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const HintText = styled.p`
  margin: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const DOMAIN_OVERRIDABLE_FIELDS: Partial<
  Record<CatalogDomain, CatalogOverridableField[]>
> = {
  [CatalogDomain.PRODUCTS]: [
    CatalogOverridableField.UNIT_PRICE,
    CatalogOverridableField.TAKEAWAY_PRICE,
    CatalogOverridableField.PREP_STATION_ID,
    CatalogOverridableField.BOOKKEEPING_ACCOUNT_ID,
    CatalogOverridableField.NAME,
  ],
  [CatalogDomain.BOOKKEEPING_ACCOUNTS]: [
    CatalogOverridableField.NAME,
    CatalogOverridableField.ACCOUNT_NUMBER,
  ],
  [CatalogDomain.PREP_STATIONS]: [
    CatalogOverridableField.PRINTER_IDS,
    CatalogOverridableField.PRINTER_OVERRIDES,
    CatalogOverridableField.IS_MASTER,
    CatalogOverridableField.MIRRORED_PREP_STATION_IDS,
  ],
  [CatalogDomain.OFFERS]: [
    CatalogOverridableField.NAME,
    CatalogOverridableField.ENABLED,
    CatalogOverridableField.PRIORITY,
  ],
  [CatalogDomain.PRODUCT_PAGES]: [CatalogOverridableField.NAME],
};

const mergeDomainPolicy = (
  domain?: CatalogDomainPolicy,
): CatalogDomainPolicy => ({
  storesCanCreateLocal:
    domain?.storesCanCreateLocal ?? defaultDomainPolicy.storesCanCreateLocal,
  overridableFields:
    domain?.overridableFields ?? defaultDomainPolicy.overridableFields,
});

const mergeCatalogPolicy = (policy?: CatalogPolicy): CatalogPolicy => ({
  products: mergeDomainPolicy(policy?.products),
  productGroups: mergeDomainPolicy(policy?.productGroups),
  modifiers: mergeDomainPolicy(policy?.modifiers),
  modifierGroups: mergeDomainPolicy(policy?.modifierGroups),
  courses: mergeDomainPolicy(policy?.courses),
  units: mergeDomainPolicy(policy?.units),
  suppliers: mergeDomainPolicy(policy?.suppliers),
  extraProductFields: mergeDomainPolicy(policy?.extraProductFields),
  bookkeepingAccounts: mergeDomainPolicy(policy?.bookkeepingAccounts),
  prepStations: mergeDomainPolicy(policy?.prepStations),
  offers: mergeDomainPolicy(policy?.offers),
  productPages: mergeDomainPolicy(policy?.productPages),
  layouts: mergeDomainPolicy(policy?.layouts),
});

type ChainCatalogPolicyFormProps = {
  catalogPolicy?: CatalogPolicy;
  onSubmit: (policy: CatalogPolicy) => void;
  isLoading?: boolean;
  errorMessage?: string;
};

export const ChainCatalogPolicyForm = ({
  catalogPolicy,
  onSubmit,
  isLoading,
  errorMessage,
}: ChainCatalogPolicyFormProps) => {
  const { t } = useTranslation();
  const [policy, setPolicy] = useState<CatalogPolicy>(() =>
    mergeCatalogPolicy(catalogPolicy ?? defaultCatalogPolicy),
  );

  const updateDomain = (
    domainKey: keyof CatalogPolicy,
    patch: Partial<CatalogDomainPolicy>,
  ) => {
    setPolicy((current) => ({
      ...current,
      [domainKey]: {
        ...current[domainKey],
        ...patch,
      },
    }));
  };

  const toggleOverridableField = (
    domainKey: keyof CatalogPolicy,
    field: CatalogOverridableField,
    enabled: boolean,
  ) => {
    const currentFields = policy[domainKey].overridableFields ?? [];
    const nextFields = enabled
      ? currentFields.includes(field)
        ? currentFields
        : [...currentFields, field]
      : currentFields.filter((currentField) => currentField !== field);
    updateDomain(domainKey, { overridableFields: nextFields });
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit(policy);
      }}
    >
      {errorMessage ? <InfoBox variant="error">{errorMessage}</InfoBox> : null}
      <DomainList>
        {Object.values(CatalogDomain).map((domain) => {
          const domainKey = catalogDomainPolicyKey[domain];
          const domainPolicy = policy[domainKey];
          const overridableFields = DOMAIN_OVERRIDABLE_FIELDS[domain] ?? [];
          return (
            <DomainCard key={domain}>
              <DomainTitle>
                {t(`chainSettings.domains.${domainKey}`)}
              </DomainTitle>
              <FormGroup>
                <ToggleRow>
                  <FormLabel>
                    {t("chainSettings.storesCanCreateLocal")}
                  </FormLabel>
                  <Toggle
                    checked={domainPolicy.storesCanCreateLocal !== false}
                    onChange={(checked) =>
                      updateDomain(domainKey, {
                        storesCanCreateLocal: checked,
                      })
                    }
                  />
                </ToggleRow>
                <HintText>
                  {t("chainSettings.storesCanCreateLocalHint")}
                </HintText>
              </FormGroup>
              {overridableFields.length > 0 ? (
                <FormGroup>
                  <FormLabel>{t("chainSettings.overridableFields")}</FormLabel>
                  <HintText>{t("chainSettings.overridableFieldsHint")}</HintText>
                  <CheckboxGroup>
                    {overridableFields.map((field) => {
                      const checked = (
                        domainPolicy.overridableFields ?? []
                      ).includes(field);
                      return (
                        <CheckboxLabel key={field}>
                          <Checkbox
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              toggleOverridableField(
                                domainKey,
                                field,
                                e.target.checked,
                              )
                            }
                          />
                          {t(`chainSettings.fields.${field}`)}
                        </CheckboxLabel>
                      );
                    })}
                  </CheckboxGroup>
                </FormGroup>
              ) : null}
            </DomainCard>
          );
        })}
      </DomainList>
      <FormActions>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.saving") : t("common.saveChanges")}
        </Button>
      </FormActions>
    </Form>
  );
};
